import { useState } from 'react';
import * as Y from 'yjs';
import { boardMap, columnSchema } from '../../shared/doc.js';
import { columnCards, deleteColumn, renameColumn } from '../../shared/ops.js';
import type { ProjectConn } from '../doc/useProject.js';
import { CardFrame, type FrameProps } from './CardFrame.js';

type Shared = Omit<FrameProps, 'cardId' | 'placement' | 'selected' | 'editing' | 'movedBy'>;

export function Columns(props: Shared & {
  boardId: string;
  selection: Set<string>;
  editingId: string | null;
  movedBy: (cardId: string) => FrameProps['movedBy'];
  onColumnPointerDown: (e: React.PointerEvent, columnId: string) => void;
}) {
  const { conn, boardId } = props;
  const cols = boardMap(conn.doc, boardId)?.get('columns') as Y.Map<Y.Map<any>> | undefined;
  if (!cols) return null;
  return (
    <>
      {[...cols.entries()].map(([id, col]) => {
        // Unknown or malformed column data (a future version, or a buggy agent) is skipped
        // rather than dereferenced — the rest of the board must keep rendering. A column value
        // that isn't even a Y.Map (so has no .toJSON()) is checked first, then validated before
        // calling columnCards, which itself assumes a well-shaped 'cardIds' array.
        if (!(col instanceof Y.Map) || !columnSchema.safeParse(col.toJSON()).success) return null;
        return <Column key={id} {...props} columnId={id} col={col} cardIds={columnCards(conn.doc, boardId, id)} />;
      })}
    </>
  );
}

function Column(props: Shared & {
  boardId: string;
  columnId: string;
  col: Y.Map<any>;
  cardIds: string[];
  selection: Set<string>;
  editingId: string | null;
  movedBy: (cardId: string) => FrameProps['movedBy'];
  onColumnPointerDown: (e: React.PointerEvent, columnId: string) => void;
}) {
  const { conn, boardId, columnId, col } = props;
  const [renaming, setRenaming] = useState(false);
  const pos = col.get('pos') as { x: number; y: number };
  return (
    <section className="column" data-column-id={columnId} style={{ left: pos.x, top: pos.y, width: col.get('w') }}>
      <header onPointerDown={(e) => props.onColumnPointerDown(e, columnId)} onDoubleClick={() => setRenaming(true)}>
        {renaming ? (
          <input
            aria-label="Column title"
            autoFocus
            defaultValue={col.get('title')}
            onPointerDown={(e) => e.stopPropagation()}
            onBlur={(e) => {
              renameColumn(conn.doc, boardId, columnId, e.target.value);
              setRenaming(false);
            }}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
          />
        ) : (
          <h3>{col.get('title') || 'Column'}</h3>
        )}
        <button className="link small" aria-label="Delete column" onPointerDown={(e) => e.stopPropagation()} onClick={() => deleteColumn(conn.doc, boardId, columnId)}>
          ×
        </button>
      </header>
      <div className="column-cards">
        {props.cardIds.map((cardId) => (
          <CardFrame
            key={cardId}
            {...props}
            cardId={cardId}
            placement={null}
            selected={props.selection.has(cardId)}
            editing={props.editingId === cardId}
            movedBy={props.movedBy(cardId)}
          />
        ))}
        {props.cardIds.length === 0 && <p className="muted">Drop cards here</p>}
      </div>
    </section>
  );
}
